import type {
  DynamicTableColumnConfig,
  DynamicTableConfig,
  DynamicTableRowConfig,
  Page,
} from '../../types';

const reportingPeriod = '1 October 2025 and 31 March 2026';
const gradeOptions = ['AO', 'EO', 'HEO', 'SEO', 'G7', 'G6', 'SCS', 'Other', 'Unknown'];
const summaryRows: DynamicTableRowConfig[] = [
  { key: 'scs', label: 'SCS' },
  { key: 'g6', label: 'G6' },
  { key: 'g7', label: 'G7' },
  { key: 'seo', label: 'SEO' },
  { key: 'heo', label: 'HEO' },
  { key: 'eo', label: 'EO' },
  { key: 'ao', label: 'AO' },
  { key: 'otherGrade', label: 'Other Grade' },
  { key: 'unknown', label: 'Unknown' },
];
const durationColumn: DynamicTableColumnConfig = {
  key: 'expectedDuration',
  label: 'Expected duration of secondment',
  minWidth: 240,
  inputType: 'duration',
  duration: {
    unitKey: 'expectedDurationUnit',
    durationDaysKey: 'secondment_duration',
    defaultUnit: 'day(s)',
    units: [
      { value: 'day(s)', days: 1 },
      { value: 'week(s)', days: 7 },
      { value: 'month(s)', days: 30 },
      { value: 'year(s)', days: 365 },
    ],
  },
};

type Direction = 'inward' | 'outward';

const buildDetailsTable = (direction: Direction): DynamicTableConfig => ({
  rowHeader: 'Secondment',
  showRowHeader: false,
  columns: [
    {
      key: 'grade',
      label: 'Grade',
      inputType: 'select',
      options: gradeOptions,
      emptyOptionLabel: '',
    },
    {
      key: 'professionFunction',
      label: 'Profession / Function',
      minWidth: 190,
      headerLineLimit: 1,
      inputType: 'text',
    },
    {
      key: 'role',
      label: 'Role',
      minWidth: 360,
      inputType: 'text',
    },
    {
      key: direction === 'inward' ? 'organisationSecondedFrom' : 'organisationSecondedTo',
      label: `Organisation seconded ${direction === 'inward' ? 'from' : 'to'} (inc. Devolved Administrations, Crown Dependencies and Government of Ireland)`,
      minWidth: 280,
      inputType: 'text',
      fillRemainingWidth: true,
    },
    durationColumn,
    {
      key: 'commencedDuringReportingPeriod',
      label: `Did this secondment commence during the period ${reportingPeriod}?`,
      headerLineLimit: 3,
      inputType: 'select',
      options: ['Yes', 'No'],
    },
  ],
  rows: [],
  continuous: true,
  initialContinuousEditableRowCount: 5,
  addRowBackground: 'lightBlue',
  headerLineLimit: 2,
  compact: true,
  stickyHeaders: true,
  highlightActive: true,
  height: 640,
  showExcelToolbar: true,
  confirmExcelImport: false,
});

const buildSummaryTable = (direction: Direction): DynamicTableConfig => ({
  rowHeader: 'Grade',
  columns: [
    {
      key: 'numberOfCurrentSecondments',
      label: 'No. of Current Secondments',
    },
  ],
  rows: summaryRows,
  compact: true,
  rowCountsFrom: {
    question: `${direction}-secondments-details`,
    sourceColumn: 'grade',
    targetColumn: 'numberOfCurrentSecondments',
    sourceValueByRow: {
      otherGrade: 'Other',
    },
  },
});

const buildSecondmentPages = (direction: Direction): Page[] => {
  const capitalisedDirection = `${direction[0].toUpperCase()}${direction.slice(1)}`;
  const movement = direction === 'inward' ? 'in your Department' : 'out of your Department';

  return [
    {
      pageType: 'question',
      title: `${capitalisedDirection} Secondments Details`,
      name: `${direction}-secondments-details-page`,
      limitWidth: false,
      description: `If known, please provide the details of the active secondments ${movement} between ${reportingPeriod} (inc. Devolved Administrations, Crown Dependencies and Government of Ireland) in the table below.`,
      questions: [
        {
          questionType: 'dynamicTable',
          title: `${capitalisedDirection} Secondments Details`,
          name: `${direction}-secondments-details`,
          dynamicTable: buildDetailsTable(direction),
          hideOptionalLabel: true,
          rules: { required: false },
        },
      ],
    },
    {
      pageType: 'question',
      title: `${capitalisedDirection} secondments summary`,
      name: `${direction}-secondments-summary-page`,
      questions: [
        {
          questionType: 'dynamicTable',
          title: `${capitalisedDirection} Secondments`,
          name: `${direction}-secondments-summary`,
          hint: `Provide the total numbers at each grade for active secondments ${movement} between ${reportingPeriod}. Counts are populated from the details table and can be changed.`,
          dynamicTable: buildSummaryTable(direction),
          hideOptionalLabel: true,
          rules: { required: true },
        },
        {
          questionType: 'textarea',
          title: `Please use the box below for any information not covered in the ${capitalisedDirection} Secondments Details tabs:`,
          name: `${direction}-secondments-notes`,
          hint: `You may wish to check the ${capitalisedDirection} Secondments Details table first so as not to duplicate information.`,
          textRows: 8,
          hideOptionalLabel: true,
          rules: { required: false },
        },
      ],
    },
  ];
};

export const inwardSecondments = buildSecondmentPages('inward');
export const outwardSecondments = buildSecondmentPages('outward');
